import { useTu } from "@src/appV2/i18n/useTu";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, TextField } from "@mui/material";
import { type TuFunction } from "@src/appV2/i18n/types";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";

function getSpendingLimitIncreaseRequestFormSchema(tu: TuFunction) {
  return z.object({
    additionalHours: z
      .number({ invalid_type_error: tu("Please enter a valid number") })
      .positive(tu("Hours must be greater than 0")),
  });
}

type SpendingLimitIncreaseRequestFormValues = z.infer<
  ReturnType<typeof getSpendingLimitIncreaseRequestFormSchema>
>;

interface SpendingLimitIncreaseRequestFormProps {
  onSubmit: (values: SpendingLimitIncreaseRequestFormValues) => void;
  isSubmitting?: boolean;
}

export function SpendingLimitIncreaseRequestForm(props: SpendingLimitIncreaseRequestFormProps) {
  const { tu } = useTu("spendingControls");
  const spendingLimitIncreaseRequestFormSchema = useMemo(
    () => getSpendingLimitIncreaseRequestFormSchema(tu),
    [tu]
  );

  const { onSubmit, isSubmitting = false } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SpendingLimitIncreaseRequestFormValues>({
    resolver: zodResolver(spendingLimitIncreaseRequestFormSchema),
    defaultValues: {
      additionalHours: undefined,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Box>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            })}
          >
            {tu("Additional hours")}
          </Text>
          <TextField
            {...register("additionalHours", { valueAsNumber: true })}
            fullWidth
            placeholder={tu("Enter value...")}
            type="number"
            error={Boolean(errors.additionalHours)}
            helperText={errors.additionalHours?.message}
          />
        </Box>

        <Box>
          <LoadingButton isLoading={isSubmitting} type="submit" variant="primary">
            {tu("Request approval")}
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
}
